import { RefreshOutlined } from "@mui/icons-material";
import NightlightIcon from "@mui/icons-material/Nightlight";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import { Box, Divider, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { type FC, useMemo } from "react";
import { useAuth } from "../../../../contexts/AuthProvider";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { useRevealWage } from "../../../../hooks/useRevealWage";
import { INK, NEUTRAL } from "../../../../theme/tokens";
import { type Nurse, type NurseInShift, ShiftType } from "../../../../utils/models";
import { isHeadNurse } from "../../../../utils/utilFunctions";
import { NurseRoleList } from "../../../NurseRoleList/NurseRoleList";
import { NurseShiftBadges } from "../../../NurseShiftBadges/NurseShiftBadges";
import { NurseUnionIndicator } from "../../../NurseUnionIndicator/NurseUnionIndicator";

type MobileNurseItemProps = {
	nurse: NurseInShift;
	onClick?: (id: string) => void;
	weekIndex: number;
	dayIndex: number;
	background?: boolean;
};

export const MobileNurseItem: FC<MobileNurseItemProps> = ({ nurse, onClick, weekIndex, background }) => {
	const { getNurseFormattedName, getNurseById } = useHospitalData();
	const { userInfo } = useAuth();
	const canViewWage = useRevealWage();
	const nurseDetails = useMemo<Nurse | undefined>(() => getNurseById(nurse.nurseId), [getNurseById, nurse.nurseId]);

	const nurseWage = nurse.cost ?? 0;

	return (
		<Box sx={{ bgcolor: background ? NEUTRAL[50] : "white", p: 2, mb: 1 }} onClick={() => isHeadNurse(userInfo.role) && onClick?.(nurse.id)}>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<NurseRoleList roles={[nurse.role]} />
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 1,
						}}
					>
						<Typography sx={{ fontSize: "14px", fontWeight: 600, color: INK.SECONDARY }}>{getNurseFormattedName(nurse.nurseId)}</Typography>
						{!!nurse.recurringShiftId && <RefreshOutlined fontSize={"inherit"} />}
						<NurseUnionIndicator nurseIdOrNurseObject={nurse.nurseId} />
					</Box>
					{nurse.location && (
						<Box sx={{ display: "flex", alignItems: "center", marginLeft: 0.8 }}>
							<Divider orientation="vertical" sx={{ mx: 1, height: "16px", backgroundColor: INK.SECONDARY }} />
							<Typography sx={{ fontSize: "12px", fontWeight: 400, color: alpha(INK.SECONDARY, 0.5) }}>
								{background ? nurse.startTime.toFormat("ccc, MMM d") : nurse.location}
							</Typography>
						</Box>
					)}
				</Box>
				{nurseDetails && isHeadNurse(userInfo.role) && (
					<Box sx={{ display: "flex", justifyContent: "flex-end", flex: 1 }}>
						<NurseShiftBadges shift={nurse} weekIndex={weekIndex} disableNotOnboardedBadge={true} />
					</Box>
				)}
			</Box>
			<Box
				sx={{
					display: "flex",
					fontWeight: 400,
					color: alpha(INK.SECONDARY, 0.5),
					justifyContent: "space-between",
					alignItems: "center",
				}}
			>
				<Box sx={{ display: "flex", gap: 1.2 }}>
					{nurse.shiftType === ShiftType.Night && <NightlightIcon sx={{ fontSize: 15, opacity: "50%" }} />}
					{nurse.shiftType === ShiftType.Day && <WbSunnyIcon sx={{ fontSize: 15, opacity: "50%" }} />}
					<Typography sx={{ fontSize: "12px" }}>
						{nurse.startTime.toFormat("h:mm a")}-{nurse.endTime.toFormat("h:mm a")} {nurse.endTime.diff(nurse.startTime, "hour").hours}h
					</Typography>
					{nurse.jobName && (
						<>
							<Divider orientation="vertical" sx={{ height: "12px", backgroundColor: alpha(INK.SECONDARY, 0.5) }} />
							<Typography sx={{ fontSize: "12px" }}>{nurse.jobName}</Typography>
						</>
					)}
				</Box>

				{canViewWage && isHeadNurse(userInfo.role) && (
					<Box>
						<Divider flexItem orientation="vertical" />
						<Typography sx={{ fontSize: "12px", fontWeight: 600 }}>
							${nurseWage.toFixed(2)} {nurse.bonus > 0 && `(+$${nurse.bonus})`}
						</Typography>
					</Box>
				)}
			</Box>
		</Box>
	);
};
